import type { StorageApi } from '@backstage/core-plugin-api';
import type { OnboardingApi } from './OnboardingApi';
import type { OnboardingState, PracticeId } from '../types';

const BUCKET = 'new-starter-onboarding';

export class OnboardingApiClient implements OnboardingApi {
  private readonly storage: StorageApi;

  constructor(options: { storageApi: StorageApi }) {
    this.storage = options.storageApi.forBucket(BUCKET);
  }

  async getState(): Promise<OnboardingState> {
    return {
      selectedPractice:
        this.storage.snapshot<PracticeId>('selectedPractice').value ?? null,
      hasCompletedSetup:
        this.storage.snapshot<boolean>('hasCompletedSetup').value ?? false,
      hasSeenOnboarding:
        this.storage.snapshot<boolean>('hasSeenOnboarding').value ?? false,
      isDismissed: this.storage.snapshot<boolean>('isDismissed').value ?? false,
      checkedItems:
        this.storage.snapshot<Record<string, string[]>>('checkedItems').value ??
        {},
    };
  }

  async setSelectedPractice(practiceId: PracticeId): Promise<void> {
    await this.storage.set('selectedPractice', practiceId);
    await this.storage.set('hasCompletedSetup', true);
  }

  async markSeenOnboarding(): Promise<void> {
    await this.storage.set('hasSeenOnboarding', true);
  }

  async dismiss(): Promise<void> {
    await this.storage.set('isDismissed', true);
  }

  async undismiss(): Promise<void> {
    await this.storage.set('isDismissed', false);
  }

  async toggleItem(
    practiceId: PracticeId,
    itemId: string,
    checked: boolean,
  ): Promise<void> {
    const current =
      this.storage.snapshot<Record<string, string[]>>('checkedItems').value ??
      {};
    const practiceItems = new Set(current[practiceId] ?? []);
    if (checked) {
      practiceItems.add(itemId);
    } else {
      practiceItems.delete(itemId);
    }
    await this.storage.set('checkedItems', {
      ...current,
      [practiceId]: [...practiceItems],
    });
  }

  async resetSetup(): Promise<void> {
    await this.storage.remove('selectedPractice');
    await this.storage.remove('hasCompletedSetup');
  }
}
